/**
 * Site-Wide Engine Bundle: nbk-portfolio
 * Architecture: Nirajan <https://nbk.com.np>
 *
 * Loads on every page (this is the theme's only global stylesheet entry
 * too — see the `../scss/main.scss` import below). Homepage-only engines
 * (the 3D hero grid, arc-cards carousel, Three.js background) live in
 * ./home.js instead, enqueued only where their DOM actually exists — see
 * functions.php's nbk_enqueue_assets().
 */

import '../scss/main.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { CustomCursor } from './cursor.js';
import { AmbientAudioController } from './ambient-audio.js';
import { IntroLoader } from './intro-loader.js';
import { ScrollRevealEngine } from './scroll-reveal.js';
import { GatewayFlow } from './gateway-flow.js';
import { initCookieConsent } from './cookie-consent.js';
import { LightboxModal } from './lightbox-modal.js';
import { CurvedProjectSlider } from './curved-project-slider.js';

gsap.registerPlugin(ScrollTrigger);

// Global State
const state = {
  lenis: null,
  ambientAudio: null,
  introLoader: null,
  isReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
};

/**
 * 1. Initialize Lenis Smooth Inertia Scroll synced to GSAP Ticker
 */
function initSmoothScroll() {
  if (state.isReducedMotion) return;

  ScrollTrigger.config({ ignoreMobileResize: true });

  state.lenis = new Lenis({
    lerp: 0.095,
    wheelMultiplier: 1.25,
    touchMultiplier: 1.8,
  });
  window.nbkLenis = state.lenis;

  state.lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    state.lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  // Smooth Anchor Navigation
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const targetId = anchor.getAttribute('href');
      if (targetId && targetId !== '#') {
        const targetEl = document.querySelector(targetId);
        if (targetEl) {
          e.preventDefault();
          state.lenis.scrollTo(targetEl, { offset: 0, duration: 1.2 });
        }
      }
    });
  });

  // Desktop Keyboard Quick-Jump Navigation (PageDown, PageUp, Home, End)
  window.addEventListener('keydown', (e) => {
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
    if (e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey)) {
      e.preventDefault();
      state.lenis.scrollTo(window.scrollY + window.innerHeight * 0.85, { duration: 0.8 });
    } else if (e.key === 'PageUp' || (e.key === ' ' && e.shiftKey)) {
      e.preventDefault();
      state.lenis.scrollTo(Math.max(0, window.scrollY - window.innerHeight * 0.85), { duration: 0.8 });
    } else if (e.key === 'Home') {
      e.preventDefault();
      state.lenis.scrollTo(0, { duration: 1.2 });
    } else if (e.key === 'End') {
      e.preventDefault();
      state.lenis.scrollTo(document.documentElement.scrollHeight, { duration: 1.2 });
    }
  });
}

/**
 * 2. Floating Navigation ScrollSpy & Active Hash State Tracker
 * Dynamically tracks which section is in view and updates the active nav pill.
 */
function initNavScrollSpy() {
  const nav = document.getElementById('site-navigation');
  if (!nav) return;

  const links = Array.from(nav.querySelectorAll('.nbk-nav-link'));
  if (!links.length) return;

  // Only run ScrollSpy on multi-section navigation (e.g. homepage).
  // Subpages have a single static link ("Back to Home" / "Back to Projects") which must retain its active pill styling.
  const isMultiSectionNav = links.some((l) => l.hasAttribute('data-section'));
  if (!isMultiSectionNav) return;

  const sections = [
    { id: 'about', el: document.getElementById('about') },
    { id: 'projects', el: document.getElementById('projects') },
    {
      id: 'approach',
      el: document.getElementById('approach') || document.getElementById('how-i-work'),
    },
    { id: 'intro', el: document.getElementById('intro') },
  ];

  // If none of these sections exist on the current page, exit gracefully
  if (!sections.some((s) => s.el)) return;

  let currentActiveId = 'intro';

  function setActive(activeId, updateUrlHash = false) {
    if (
      currentActiveId === activeId &&
      links.some(
        (l) => l.classList.contains('is-active') && l.getAttribute('data-section') === activeId,
      )
    ) {
      return;
    }
    currentActiveId = activeId;

    links.forEach((link) => {
      const section =
        link.getAttribute('data-section') || link.getAttribute('href')?.replace(/^.*#/, '');
      if (section === activeId) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove('is-active');
        link.removeAttribute('aria-current');
      }
    });

    if (updateUrlHash && window.history.replaceState) {
      window.history.replaceState(null, '', `#${activeId}`);
    }
  }

  function checkScrollPosition() {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // 1. Bottom of page threshold: activate last item (about)
    if (scrollY + viewportHeight >= documentHeight - 60) {
      setActive('about', true);
      return;
    }

    // 2. Reading focus line (40% down from viewport top)
    const threshold = viewportHeight * 0.4;

    const aboutEl = document.getElementById('about');
    const projectsEl = document.getElementById('projects');
    const howIWorkEl = document.getElementById('how-i-work');
    const approachEl =
      document.getElementById('approach') || document.getElementById('capabilities');

    if (aboutEl && aboutEl.getBoundingClientRect().top <= threshold) {
      setActive('about', true);
    } else if (projectsEl && projectsEl.getBoundingClientRect().top <= threshold) {
      setActive('projects', true);
    } else if (
      (howIWorkEl && howIWorkEl.getBoundingClientRect().top <= threshold) ||
      (approachEl && approachEl.getBoundingClientRect().top <= threshold)
    ) {
      setActive('approach', true);
    } else {
      setActive('intro', scrollY > 100);
    }
  }

  // Click handler on navigation links for instant active transition + smooth scroll
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const targetId =
        link.getAttribute('data-section') || link.getAttribute('href')?.replace(/^.*#/, '');
      const targetEl =
        document.getElementById(targetId) ||
        (targetId === 'approach' ? document.getElementById('how-i-work') : null);
      if (targetEl) {
        e.preventDefault();
        setActive(targetId, true);
        if (state.lenis) {
          state.lenis.scrollTo(targetEl, { offset: 0, duration: 1.2 });
        } else {
          targetEl.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  });

  // Bind scroll listeners
  window.addEventListener('scroll', checkScrollPosition, { passive: true });
  if (state.lenis) {
    state.lenis.on('scroll', checkScrollPosition);
  }

  // Support direct URL hash loading or manual hash changes
  const checkInitialHash = () => {
    const rawHash = window.location.hash.replace(/^#/, '');
    if (rawHash && ['intro', 'approach', 'projects', 'about'].includes(rawHash)) {
      setActive(rawHash);
    } else if (rawHash === 'how-i-work') {
      setActive('approach');
    } else {
      checkScrollPosition();
    }
  };

  window.addEventListener('hashchange', checkInitialHash);
  checkInitialHash();
}

/**
 * 3. Live Dual Time Zone Synchronizer (Kathmandu, Nepal vs Visitor Local)
 */
function initLiveClock() {
  const ktmClocks = [
    document.getElementById('nbk-ktm-clock'),
    document.getElementById('nbk-local-clock'),
  ].filter(Boolean);
  const userClock = document.getElementById('nbk-user-clock');
  const userTzName = document.getElementById('nbk-user-tz-name');
  const userOffsetStr = document.getElementById('nbk-user-offset-str');
  const timeDiffBadge = document.getElementById('nbk-time-diff-badge');
  const timeComparisonMsg = document.getElementById('nbk-time-comparison-msg');

  if (!ktmClocks.length && !userClock) return;

  // Resolve visitor timezone and city label
  let detectedTz = 'UTC';
  let cleanCity = 'Local';
  try {
    detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const parts = detectedTz.split('/');
    cleanCity = (parts[parts.length - 1] || 'Local').replace(/_/g, ' ');
  } catch (e) {
    console.warn('[main.js] Failed to resolve local timezone:', e);
  }

  if (userTzName) {
    userTzName.textContent = cleanCity;
    userTzName.setAttribute('title', detectedTz);
  }

  const updateClock = () => {
    const now = new Date();

    // 1. Kathmandu Time (UTC+5:45)
    const ktmTimeStr = new Intl.DateTimeFormat([], {
      timeZone: 'Asia/Kathmandu',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(now);

    ktmClocks.forEach((el) => {
      el.textContent = ktmTimeStr;
    });

    // 2. Visitor Local Time
    if (userClock) {
      const userTimeStr = new Intl.DateTimeFormat([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(now);
      userClock.textContent = userTimeStr;
    }

    // 3. Offset and Time Difference Calculations
    // getTimezoneOffset() returns minutes behind UTC (positive if behind UTC, negative if ahead)
    const visitorOffsetMinutes = -now.getTimezoneOffset();
    const ktmOffsetMinutes = 345; // UTC+5:45 = 5 * 60 + 45 = 345 minutes

    if (userOffsetStr) {
      const sign = visitorOffsetMinutes >= 0 ? '+' : '-';
      const absH = Math.floor(Math.abs(visitorOffsetMinutes) / 60);
      const absM = Math.abs(visitorOffsetMinutes) % 60;
      userOffsetStr.textContent = `UTC${sign}${absH}${absM ? `:${String(absM).padStart(2, '0')}` : ''}`;
    }

    const diffMinutes = ktmOffsetMinutes - visitorOffsetMinutes;
    const absDiff = Math.abs(diffMinutes);
    const diffH = Math.floor(absDiff / 60);
    const diffM = absDiff % 60;

    const timeStr = `${diffH}h${diffM ? ` ${diffM}m` : ''}`;

    if (timeDiffBadge) {
      if (diffMinutes === 0) {
        timeDiffBadge.textContent = 'SYNCED (0H)';
        timeDiffBadge.className =
          'px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-[10px] font-mono font-semibold tracking-wide';
      } else if (diffMinutes > 0) {
        timeDiffBadge.textContent = `+${timeStr} AHEAD`;
        timeDiffBadge.className =
          'px-2.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 text-[10px] font-mono font-semibold tracking-wide shadow-[0_0_10px_rgba(34,211,238,0.15)]';
      } else {
        timeDiffBadge.textContent = `-${timeStr} BEHIND`;
        timeDiffBadge.className =
          'px-2.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-400/30 text-purple-300 text-[10px] font-mono font-semibold tracking-wide shadow-[0_0_10px_rgba(168,85,247,0.15)]';
      }
    }

    if (timeComparisonMsg) {
      if (diffMinutes === 0) {
        timeComparisonMsg.textContent = 'You are in the exact same timezone as Kathmandu.';
      } else if (diffMinutes > 0) {
        timeComparisonMsg.textContent = `Kathmandu is ${timeStr} ahead of your local time.`;
      } else {
        timeComparisonMsg.textContent = `Kathmandu is ${timeStr} behind your local time.`;
      }
    }
  };

  updateClock();
  setInterval(updateClock, 1000);
}

/**
 * 3. AJAX Contact Form Dispatcher
 */
function initContactForm() {
  const form = document.getElementById('nbk-contact-form');
  const feedback = document.getElementById('contact-feedback');
  const submitBtn = document.getElementById('contact-submit-btn');

  if (!form || !feedback) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btnText = submitBtn.querySelector('#contact-btn-text') || submitBtn;
    submitBtn.disabled = true;
    btnText.textContent = 'Transmitting...';
    feedback.className = 'text-xs text-center font-mono py-2 text-purple-400 block';
    feedback.textContent = 'Securing connection & transmitting message...';

    const formData = new FormData(form);
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      message: formData.get('message'),
      company_website: formData.get('company_website') || '',
    };

    try {
      const config = window.nbkConfig || {};

      // Execute Google reCAPTCHA v3 verification if enabled
      if (
        config.recaptchaEnabled &&
        config.recaptchaSiteKey &&
        typeof window.grecaptcha !== 'undefined'
      ) {
        try {
          const recaptchaToken = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha
                .execute(config.recaptchaSiteKey, { action: 'contact_form' })
                .then(resolve)
                .catch(reject);
            });
          });
          payload.recaptcha_token = recaptchaToken;
        } catch (recaptchaErr) {
          console.warn('reCAPTCHA execution failed:', recaptchaErr);
        }
      }

      const endpoint = config.restUrl ? `${config.restUrl}contact` : '/wp-json/nbk/v1/contact';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce || '',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        feedback.className = 'text-xs text-center font-mono py-2 text-emerald-400 block';
        feedback.textContent =
          '✔ ' +
          (result.message ||
            'Inquiry transmitted successfully. Confirmation copy sent to your email.');
        form.reset();
      } else {
        throw new Error(result.message || 'Submission failed.');
      }
    } catch (error) {
      feedback.className = 'text-xs text-center font-mono py-2 text-rose-400 block';
      feedback.textContent = `✖ Error: ${error.message}`;
    } finally {
      submitBtn.disabled = false;
      btnText.textContent = 'Transmit Inquiry';
    }
  });
}

/**
 * Bootstrap on DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', () => {
  // 1. Smooth Scroll & Kinetics
  initSmoothScroll();
  initNavScrollSpy();

  // 2. Contextual Cursor & Physics
  new CustomCursor();

  // 3. Ambient Music Beat Controller & Waveform (Plays automatically in loop)
  state.ambientAudio = new AmbientAudioController();

  // 4. Cinematic Full-Screen Namaste Intro Loader & Universal Page
  // Transitions (the loader itself is homepage-only; the transition
  // overlay it also drives applies to every page)
  state.introLoader = new IntroLoader(() => {
    bootScrollReveal();
    scheduleScrollTriggerRefresh(100);
  });

  initLiveClock();
  initContactForm();

  // 5. Majestic Gateway Flow & Horizon Particle Engine
  new GatewayFlow('footer-flow-canvas');

  // 6. Sticky Bottom-Left Cookie Consent Controller
  initCookieConsent();

  // 7. Global Lightbox Modal & Project Showcase Sliders
  if (!window.nbkLightbox) {
    window.nbkLightbox = new LightboxModal();
  }
  new CurvedProjectSlider('.curved-slider-container');

  // Support click-to-enlarge on static media with [data-lightbox-trigger]
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-lightbox-trigger]');
    if (!trigger) return;
    e.preventDefault();

    let images = [];
    const rawImages = trigger.getAttribute('data-images');
    if (rawImages) {
      try {
        images = JSON.parse(rawImages);
      } catch {
        images = [];
      }
    }
    const img = trigger.querySelector('img');
    if (!images.length && img) {
      images = [{ url: img.src, alt: img.alt || '' }];
    }
    const title = trigger.getAttribute('data-title') || img?.alt || '';
    const link = trigger.getAttribute('data-link') || '';
    const btnText = trigger.getAttribute('data-btn-text') || 'VIEW CASE STUDY';

    if (!window.nbkLightbox) {
      window.nbkLightbox = new LightboxModal();
    }
    if (window.nbkLightbox && images.length) {
      window.nbkLightbox.open({
        images,
        index: 0,
        title,
        link,
        btnText,
      });
    }
  });

  // Recalibrate scroll measurements with Lenis
  setTimeout(() => {
    bootScrollReveal();
    scheduleScrollTriggerRefresh(120);
  }, 150);
});

let refreshTimeout = null;
export function scheduleScrollTriggerRefresh(delay = 100) {
  if (refreshTimeout && typeof window !== 'undefined') {
    window.clearTimeout(refreshTimeout);
  }
  if (typeof window !== 'undefined') {
    refreshTimeout = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, delay);
  }
}

function bootScrollReveal() {
  if (state.scrollReveal) return;
  try {
    state.scrollReveal = new ScrollRevealEngine();
  } catch (error) {
    console.error('[main.js] ScrollRevealEngine failed to initialize.', error);
  }
}

if (document.readyState === 'complete') {
  bootScrollReveal();
} else {
  window.addEventListener('load', () => {
    bootScrollReveal();
    scheduleScrollTriggerRefresh(80);
  });
}

// Recalibrate pin spacers and bounds cleanly on dynamic viewport width changes
let lastViewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
if (typeof window !== 'undefined') {
  window.addEventListener(
    'resize',
    () => {
      if (window.innerWidth !== lastViewportWidth) {
        lastViewportWidth = window.innerWidth;
        scheduleScrollTriggerRefresh(150);
      }
    },
    { passive: true },
  );
}
